import React, { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { UserX, UserPlus, Ban, Shield, Loader2 } from "lucide-react";

interface ChallengeClaimManagerProps {
  challengeId: string;
  claimedBy: string | null;
  claimantEmail?: string | null;
  onUpdate: () => void;
}

interface UserSearchResult {
  user_id: string;
  email: string;
  display_name: string | null;
}

const ChallengeClaimManager: React.FC<ChallengeClaimManagerProps> = ({
  challengeId,
  claimedBy,
  claimantEmail,
  onUpdate,
}) => {
  const [isUnclaiming, setIsUnclaiming] = useState(false);
  const [showUnclaimConfirm, setShowUnclaimConfirm] = useState(false);
  
  // Reassign state
  const [showReassign, setShowReassign] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [reassigning, setReassigning] = useState(false);
  
  // Block state
  const [showBlock, setShowBlock] = useState(false);
  const [blockScope, setBlockScope] = useState<"challenge" | "global">("challenge");
  const [blockReason, setBlockReason] = useState("");
  const [blocking, setBlocking] = useState(false);

  const searchUsers = async (term: string) => {
    if (term.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const { data, error } = await supabase.rpc("search_users_for_invite", {
        search_term: term,
        limit_count: 10,
      });

      if (error) throw error;
      setSearchResults(data || []);
    } catch (error) {
      console.error("Error searching users:", error);
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (searchTerm) {
        searchUsers(searchTerm);
      }
    }, 300);

    return () => clearTimeout(debounce);
  }, [searchTerm]);

  const handleUnclaim = async () => {
    setIsUnclaiming(true);
    try {
      const { error } = await supabase
        .from("challenges")
        .update({
          claimed_by: null,
          status: "unclaimed",
          claimed_at: null,
        })
        .eq("id", challengeId);

      if (error) throw error;
      toast.success("Challenge unclaimed");
      setShowUnclaimConfirm(false);
      onUpdate();
    } catch (error) {
      console.error("Error unclaiming:", error);
      toast.error("Failed to unclaim challenge");
    } finally {
      setIsUnclaiming(false);
    }
  };

  const handleReassign = async (newUserId: string, newUserEmail: string) => {
    setReassigning(true);
    try {
      const { error } = await supabase
        .from("challenges")
        .update({
          claimed_by: newUserId,
          claimed_at: new Date().toISOString(),
        })
        .eq("id", challengeId);

      if (error) throw error;
      toast.success(`Challenge reassigned to ${newUserEmail}`);
      setShowReassign(false);
      setSearchTerm("");
      setSearchResults([]);
      onUpdate();
    } catch (error) {
      console.error("Error reassigning:", error);
      toast.error("Failed to reassign challenge");
    } finally {
      setReassigning(false);
    }
  };

  const handleBlockUser = async () => {
    if (!claimedBy) return;
    
    setBlocking(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("challenge_claim_blocks")
        .insert({
          user_id: claimedBy,
          challenge_id: blockScope === "challenge" ? challengeId : null,
          blocked_by: user.id,
          reason: blockReason || null,
        });

      if (error) {
        if (error.code === "23505") {
          toast.error("User is already blocked");
        } else {
          throw error;
        }
      } else {
        toast.success(
          blockScope === "global"
            ? "User blocked from all challenges"
            : "User blocked from this challenge"
        );
        
        // Also unclaim the challenge
        await supabase
          .from("challenges")
          .update({
            claimed_by: null,
            status: "unclaimed",
            claimed_at: null,
          })
          .eq("id", challengeId);
        
        setShowBlock(false);
        setBlockReason("");
        onUpdate();
      }
    } catch (error) {
      console.error("Error blocking user:", error);
      toast.error("Failed to block user");
    } finally {
      setBlocking(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-border/50">
      <div className="flex items-center gap-1 text-sm text-muted-foreground mr-2">
        <Shield className="h-4 w-4" />
        <span>Admin Actions:</span>
      </div>

      {/* Unclaim Button */}
      {claimedBy && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowUnclaimConfirm(true)}
          className="gap-1"
        >
          <UserX className="h-3 w-3" />
          Unclaim
        </Button>
      )}

      {/* Reassign Popover */}
      <Popover open={showReassign} onOpenChange={setShowReassign}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="gap-1">
            <UserPlus className="h-3 w-3" />
            {claimedBy ? "Reassign" : "Assign"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80">
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Search for user</Label>
              <Input
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            {searching && (
              <div className="flex items-center justify-center py-2">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            )}
            
            {searchResults.length > 0 && (
              <div className="max-h-48 overflow-y-auto space-y-1">
                {searchResults.map((user) => (
                  <Button
                    key={user.user_id}
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start text-left"
                    disabled={reassigning}
                    onClick={() => handleReassign(user.user_id, user.email)}
                  >
                    <div className="truncate">
                      <div className="font-medium truncate">
                        {user.display_name || user.email}
                      </div>
                      {user.display_name && (
                        <div className="text-xs text-muted-foreground truncate">
                          {user.email}
                        </div>
                      )}
                    </div>
                  </Button>
                ))}
              </div>
            )}
            
            {searchTerm.length >= 2 && searchResults.length === 0 && !searching && (
              <p className="text-sm text-muted-foreground text-center py-2">
                No users found
              </p>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {/* Block User Popover */}
      {claimedBy && (
        <Popover open={showBlock} onOpenChange={setShowBlock}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="gap-1 text-destructive hover:text-destructive">
              <Ban className="h-3 w-3" />
              Block User
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80">
            <div className="space-y-3">
              <div className="space-y-1">
                <Label>Block scope</Label>
                <Select
                  value={blockScope}
                  onValueChange={(v) => setBlockScope(v as "challenge" | "global")}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="challenge">This challenge only</SelectItem>
                    <SelectItem value="global">All challenges</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1">
                <Label>Reason (optional)</Label>
                <Textarea
                  placeholder="Why is this user being blocked?"
                  value={blockReason}
                  onChange={(e) => setBlockReason(e.target.value)}
                  rows={2}
                />
              </div>
              
              <p className="text-xs text-muted-foreground">
                Blocking will also unclaim the challenge from this user.
              </p>
              
              <Button
                variant="destructive"
                size="sm"
                className="w-full"
                onClick={handleBlockUser}
                disabled={blocking}
              >
                {blocking ? "Blocking..." : "Block & Unclaim"}
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      )}

      {/* Unclaim Confirmation Dialog */}
      <AlertDialog open={showUnclaimConfirm} onOpenChange={setShowUnclaimConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unclaim Challenge</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove {claimantEmail || "the current user"} as the owner of this
              challenge and set the status back to "Unclaimed". Are you sure?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleUnclaim} disabled={isUnclaiming}>
              {isUnclaiming ? "Unclaiming..." : "Unclaim"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ChallengeClaimManager;
